import React, { useEffect, useMemo, useState } from 'react';
import { RedirectToSignIn, useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useFluidFormConfigClient } from './fluidFormConfig.client';
import { SimpleFormProvider } from '../fluidComponents/fluidForm/SimpleFormLoader';
import AddFormModal from '../components/AddFormModal';
import { DescriptionWithTags } from '../components/DescriptionWithTags';
import { Copy } from 'lucide-react';
import { getAxiosClient } from '../http.client';
export const TemplatePreviewPage = () => {
    const { orgId, formKey } = useParams();
    const client = useFluidFormConfigClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoaded, isSignedIn } = useUser();
    const [template, setTemplate] = useState(null);
    const [publicMeta, setPublicMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [goAuth, setGoAuth] = useState(false);
    const openCopyAfterAuth = useMemo(() => {
        const params = new URLSearchParams(location.search || '');
        return params.get('openCopy') === '1';
    }, [location.search]);
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                if (!orgId || !formKey) {
                    setError('Missing template identifiers.');
                    return;
                }
                if (isSignedIn) {
                    const t = await client.getTemplate(orgId, formKey);
                    if (!mounted)
                        return;
                    setTemplate(t);
                    setPublicMeta(null);
                }
                else {
                    const publicClient = getAxiosClient('public/templates');
                    const resp = await publicClient.get(`/${encodeURIComponent(orgId)}/${encodeURIComponent(formKey)}`);
                    if (!mounted)
                        return;
                    setPublicMeta(resp.data);
                    setTemplate(null);
                }
            }
            catch (e) {
                if (!mounted)
                    return;
                setError('Failed to load template.');
            }
            finally {
                if (!mounted)
                    return;
                setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [orgId, formKey, isSignedIn]);
    // If user returned from Clerk with ?openCopy=1, open the modal.
    useEffect(() => {
        if (!isLoaded || !isSignedIn)
            return;
        if (openCopyAfterAuth) {
            setCopyModalOpen(true);
        }
    }, [isLoaded, isSignedIn, openCopyAfterAuth]);
    const handleCopyWithFormModal = async (newForm) => {
        if (!template) {
            throw new Error('Template not loaded.');
        }
        const key = String(newForm.formKey || '').trim().toLowerCase();
        const label = String(newForm.label || '').trim();
        const payload = {
            formKey: key,
            label,
            formConfig: {
                ...template.formConfig,
                key,
                label,
            },
            actionsConfig: template.actionsConfig,
            styleConfig: template.styleConfig,
            code: template.code,
        };
        // @TODO: FLU-193 Handle this.
        await client.createFormConfig(payload);
        navigate(`/form-config/${key}`);
    };
    const displayName = template?.name ||
        publicMeta?.name ||
        template?.formKey ||
        publicMeta?.formKey ||
        'Template';
    const displayDescription = template?.formConfig?.description || publicMeta?.description || '';
    const signature = template?.templateSignature || publicMeta?.templateSignature;
    const effectiveOrgId = String((template?.orgId || publicMeta?.orgId || orgId || ''));
    const effectiveFormKey = String((template?.formKey || publicMeta?.formKey || formKey || ''));
    const onUseTemplateClick = () => {
        if (!isSignedIn) {
            setGoAuth(true);
            return;
        }
        setCopyModalOpen(true);
    };
    if (goAuth) {
        const redirectUrl = `${location.pathname}?openCopy=1`;
        return React.createElement(RedirectToSignIn, { redirectUrl: redirectUrl });
    }
    return (React.createElement(PageLayout, { title: "Template Preview", icon: "clone outline", showBackButton: true, onBackClick: () => navigate(-1), error: error || undefined },
        React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6" },
            React.createElement("div", { className: "flex flex-col md:flex-row md:items-center md:justify-between gap-4" },
                React.createElement("div", { className: "min-w-0" },
                    React.createElement("div", { className: "text-2xl font-bold text-gray-900" }, displayName),
                    React.createElement("div", { className: "mt-2" },
                        React.createElement(DescriptionWithTags, { description: displayDescription }))),
                React.createElement("div", { className: "flex items-center justify-end self-start md:self-auto" },
                    React.createElement("button", { onClick: onUseTemplateClick, disabled: loading || (!template && !publicMeta), className: `inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${loading || (!template && !publicMeta)
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md'}` },
                        React.createElement(Copy, { className: "w-4 h-4" }),
                        "Use this template")))),
        React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm p-6" }, loading ? (React.createElement("div", { className: "text-gray-600" }, "Loading template\u2026")) : !template && !publicMeta ? (React.createElement("div", { className: "text-gray-600" }, "Template not available.")) : !signature ? (React.createElement("div", { className: "text-gray-600" }, "Template signature missing. Please re-toggle \u201CMake Template\u201D to generate it.")) : (React.createElement(SimpleFormProvider, { formKey: effectiveFormKey, signature: signature, autosave: true, headless: false, formInstance: `template-preview:${effectiveOrgId}:${effectiveFormKey}`, formProps: {
                showSubmitButton: true,
                includeChat: false,
            } }))),
        React.createElement(AddFormModal, { open: copyModalOpen, onClose: () => setCopyModalOpen(false), title: "Create Form from Template", saveLabel: "Create Form", initialForm: {
                label: template?.name ? `${template.name} Copy` : `${template?.formKey || ''} Copy`,
            }, onSave: handleCopyWithFormModal })));
};
export default TemplatePreviewPage;
